<template>
  <div class="rag-page">
    <section class="rag-sidebar">
      <div class="rag-header">
        <h1>文档问答 RAG</h1>
        <p>上传 PDF 后，围绕文档内容连续追问。</p>
      </div>

      <el-upload
        drag
        :auto-upload="false"
        :limit="1"
        accept="application/pdf"
        :on-change="handleFileChange"
        :on-remove="clearDocument"
      >
        <el-icon class="upload-icon"><UploadFilled /></el-icon>
        <div class="el-upload__text">拖入 PDF 或点击选择</div>
      </el-upload>

      <el-button
        class="upload-button"
        type="primary"
        :loading="uploading"
        :disabled="!selectedFile || Boolean(documentInfo)"
        @click="uploadDocument"
      >
        上传并用于问答
      </el-button>

      <div v-if="documentInfo" class="document-card">
        <strong>{{ documentInfo.filename }}</strong>
        <span>{{ formatSize(documentInfo.size) }}</span>
      </div>
    </section>

    <section class="rag-chat">
      <div class="messages" ref="messageList">
        <div
          v-for="message in messages"
          :key="message.id"
          class="message"
          :class="`message--${message.role}`"
        >
          <div class="bubble">
            <div class="message-role">{{ message.role === "user" ? "我" : "AI" }}</div>
            <div class="message-content">{{ message.content }}</div>
          </div>
        </div>
      </div>

      <form class="composer" @submit.prevent="ask">
        <el-input
          v-model="question"
          type="textarea"
          :rows="3"
          resize="none"
          placeholder="例如：这份 PDF 的核心结论是什么？有哪些风险点？"
          :disabled="answering"
        />
        <el-button
          type="primary"
          :loading="answering"
          :disabled="!canAsk"
          @click="ask"
        >
          提问
        </el-button>
      </form>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from "vue";
import { ElMessage, type UploadFile } from "element-plus";
import { UploadFilled } from "@element-plus/icons-vue";
import { useAnonymousUser } from "~/composables/useAnonymousUser";

type RagMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
};

type DocumentInfo = {
  url: string;
  filename: string;
  size: number;
};

const { getAnonymousId } = useAnonymousUser();
const selectedFile = ref<File | null>(null);
const documentInfo = ref<DocumentInfo | null>(null);
const question = ref("");
const uploading = ref(false);
const answering = ref(false);
const messageList = ref<HTMLElement | null>(null);
const messages = ref<RagMessage[]>([
  {
    id: "welcome",
    role: "assistant",
    content: "先上传一份 PDF，然后我会基于文档内容回答你的追问。",
  },
]);

const canAsk = computed(() =>
  Boolean(documentInfo.value && question.value.trim() && !answering.value),
);

function handleFileChange(uploadFile: UploadFile) {
  const raw = uploadFile.raw;
  if (!raw) return;
  if (raw.type !== "application/pdf") {
    ElMessage.error("目前仅支持 PDF 文档");
    return;
  }
  selectedFile.value = raw;
  documentInfo.value = null;
}

function clearDocument() {
  selectedFile.value = null;
  documentInfo.value = null;
}

async function uploadDocument() {
  if (!selectedFile.value) return;

  uploading.value = true;
  try {
    const formData = new FormData();
    formData.append("file", selectedFile.value);
    formData.append("type", "doc");

    const response: any = await $fetch("/api/upload/file", {
      method: "POST",
      body: formData,
    });

    if (!response.success || !response.url) {
      throw new Error(response.errorMessage || "PDF 上传失败");
    }

    documentInfo.value = {
      url: response.url,
      filename: selectedFile.value.name,
      size: selectedFile.value.size,
    };
    ElMessage.success("PDF 已上传，可以开始追问");
  } catch (err: any) {
    ElMessage.error(err?.message || "PDF 上传失败");
  } finally {
    uploading.value = false;
  }
}

async function ask() {
  if (!canAsk.value || !documentInfo.value) return;

  const userQuestion = question.value.trim();
  question.value = "";
  messages.value.push({
    id: crypto.randomUUID(),
    role: "user",
    content: userQuestion,
  });
  answering.value = true;
  await scrollToBottom();

  try {
    const response: any = await $fetch("/api/rag/chat", {
      method: "POST",
      body: {
        userId: getAnonymousId(),
        fileUrl: documentInfo.value.url,
        filename: documentInfo.value.filename,
        messages: messages.value
          .filter((message) => message.id !== "welcome")
          .map(({ role, content }) => ({ role, content })),
      },
    });

    if (!response.success) {
      throw new Error("文档问答失败");
    }

    messages.value.push({
      id: crypto.randomUUID(),
      role: "assistant",
      content: response.data.answer || "文档中没有找到足够信息。",
    });
  } catch (err: any) {
    ElMessage.error(err?.statusMessage || err?.message || "文档问答失败");
    messages.value.push({
      id: crypto.randomUUID(),
      role: "assistant",
      content: "这次文档问答没有成功，请稍后重试或换一份 PDF。",
    });
  } finally {
    answering.value = false;
    await scrollToBottom();
  }
}

async function scrollToBottom() {
  await nextTick();
  if (messageList.value) {
    messageList.value.scrollTop = messageList.value.scrollHeight;
  }
}

function formatSize(size: number) {
  return `${(size / 1024 / 1024).toFixed(2)} MB`;
}
</script>

<style scoped lang="scss">
.rag-page {
  display: grid;
  grid-template-columns: 340px minmax(0, 1fr);
  height: calc(100vh - 68px);
  background: #f6f7f9;
}

.rag-sidebar {
  padding: 24px;
  background: #fff;
  border-right: 1px solid #e4e7ed;
}

.rag-header {
  margin-bottom: 18px;

  h1 {
    margin: 0 0 6px;
    color: #303133;
    font-size: 24px;
  }

  p {
    margin: 0;
    color: #606266;
    font-size: 14px;
  }
}

.upload-icon {
  color: #409eff;
  font-size: 42px;
}

.upload-button {
  width: 100%;
  margin: 14px 0;
}

.document-card {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 14px;
  color: #303133;
  background: #f8fafc;
  border: 1px solid #e4e7ed;
  border-radius: 8px;

  span {
    color: #909399;
    font-size: 13px;
  }
}

.rag-chat {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.messages {
  flex: 1;
  overflow-y: auto;
  padding: 24px;
}

.message {
  display: flex;
  margin-bottom: 16px;

  &--user {
    justify-content: flex-end;

    .bubble {
      background: #ecf5ff;
      border-color: #c6e2ff;
    }
  }
}

.bubble {
  max-width: min(720px, 86%);
  padding: 14px 16px;
  background: #fff;
  border: 1px solid #e4e7ed;
  border-radius: 8px;
}

.message-role {
  margin-bottom: 6px;
  color: #909399;
  font-size: 12px;
}

.message-content {
  color: #303133;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

.composer {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 96px;
  gap: 12px;
  padding: 16px 24px 24px;
  background: #fff;
  border-top: 1px solid #e4e7ed;
}

@media (max-width: 900px) {
  .rag-page {
    grid-template-columns: 1fr;
    overflow-y: auto;
  }

  .rag-sidebar {
    border-right: none;
    border-bottom: 1px solid #e4e7ed;
  }

  .rag-chat {
    min-height: 560px;
  }
}
</style>
